import { Base } from '@studiometa/js-toolkit';
import LazyLoad from 'vanilla-lazyload';
// import YTPlayer from 'yt-player';
import { gsap } from 'gsap';
import { Modal } from '@studiometa/ui';
import Cookies from 'js-cookie';
import Plyr from 'plyr';
import { isDev } from './config.js';
import FormDl from './components/FormDl.js';
import Slider from './components/Slider.js';
import Popin from './components/Popin.js';
import Filters from './components/Filters.js';
import Search from './components/Search.js';

/**
 * Main App class.
 */
class App extends Base {
  /**
   * App config.
   * @returns {object}
   */
  static config = {
    log: isDev(),
    name: 'App',
    components: {
      Filters,
      Modal,
      FormDl,
      Slider,
      Popin,
      Search,
    },
    options: {
      yturl: { type: String },
    },
    refs: [
      'body',
      'header',
      'textarea',
      'toggle',
      'submenu[]',
      'player[]',
      'footermenu[]',
      'welcome',
    ],
  };

  /**
   * Log a nice message when app is ready.
   * @returns {void}
   */
  mounted() {
    this.$log('mounted 🎉');

    // Mobile submenu toggle - using event delegation as fallback
    document.addEventListener('click', (e) => {
      const submenuTrigger = e.target.closest('[data-ref="submenu[]"]');
      if (submenuTrigger) {
        e.preventDefault();
        submenuTrigger.classList.toggle('active');
        const next = submenuTrigger.nextElementSibling;
        if (next && next.classList.contains('submenu')) {
          next.classList.toggle('h-0');
        }
      }

      // Footer menu accordion
      const footermenuTrigger = e.target.closest('[data-ref="footermenu[]"]');
      if (footermenuTrigger) {
        e.preventDefault();
        const next = footermenuTrigger.nextElementSibling;
        if (next) {
          let height = 0;
          for (let i = 0; i < next.children.length; i += 1) {
            height += Number.parseFloat(next.children[i].offsetHeight);
          }
          gsap.to(next, { duration: 0.3, height: next.offsetHeight === 0 ? `${height}px` : '0px' });
        }
      }
    });

    // Welcome popin
    if (this.$refs.welcome && Cookies.get('welcome-popin') === undefined) {
      setTimeout(() => {
        this.$refs.welcome.open();

        const duration = Number.parseFloat(this.$refs.welcome.$el.dataset.duration);
        if (duration > 0) {
          setTimeout(() => {
            this.$refs.welcome.close();
          }, duration * 1000);
        }
      }, 300);
    }

    // Lazyload
    this.lazyload = new LazyLoad({
      elements_selector: '.js-lazy',
      threshold: 600,

      callback_enter: ($el) => {
        if ($el.classList.contains('js-lazy-error-placeholder')) {
          $el.lazyLoadPlaceholder = $el.src;
        }
      },

      callback_error: ($el) => {
        if ($el.lazyLoadPlaceholder) {
          $el.src = $el.lazyLoadPlaceholder;
        }
      },
    });

    document.addEventListener('slider:ready', (e) => {
      const sliderEl = e.target;
      const lazyImages = sliderEl.querySelectorAll('.js-lazy');
      lazyImages.forEach((img) => {
        if (img.dataset.src) {
          img.src = img.dataset.src;
          delete img.dataset.src;
          img.classList.remove('js-lazy');
        }
      });
    });

    // Fallback: force load any remaining lazy images after page is fully loaded
    window.addEventListener('load', () => {
      setTimeout(() => {
        this.lazyload.update();
        this.lazyload.loadAll();
      }, 100);
    });

    // Youtube player
    if (this.$refs.player) {
      this.$refs.player.forEach((p) => {
        const plyr = new Plyr(p, {
          controls: ['play-large', 'progress', 'current-time', 'mute', 'volume'],
          youtube: {
            controls: false,
            modestbranding: false,
            showinfo: false,

            iv_load_policy: false,
          },
        });
        plyr.stop();

        // return;
        // const player = new YTPlayer(p.querySelector('.player__content'), {
        //   controls: false,
        //   modestBranding: false,
        //   related: false,
        //   annotations: false,
        // });

        // player.load(p.dataset.ytid);

        // const trigger = p.querySelector('.trigger-play');
        // trigger.addEventListener('click', (e) => {
        //   e.preventDefault();
        //   player.play();
        // });
        // player.on('playing', () => {
        //   p.classList.add('onplaying');
        // });
        // player.on('paused', () => {
        //   p.classList.remove('onplaying');
        // });
      });
    }
  }

  /** */
  onToggleClick(e) {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    this.$refs.body.classList.toggle('open-menu');
  }

  /**
   * Submenu mobile submenu
   * @param {Event} e
   * @param {number} index
   */
  onSubmenuClick(e, index) {
    // Prevent default if e is an event
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    // Get submenu element from refs using index
    const submenu = this.$refs.submenu[index];
    if (!submenu) return;

    submenu.classList.toggle('active');

    const next = submenu.nextElementSibling;
    if (next) {
      next.classList.toggle('h-0');
    }
  }

  /** */
  onFiltersUpdated() {
    this.lazyload.update();
  }

  /**
   * Fix menu when scrolling
   */
  scrolled() {
    if (window.scrollY > 0) {
      this.$refs.body.classList.add('fixed-menu');
    } else {
      this.$refs.body.classList.remove('fixed-menu');
    }
  }

  /**
   * Mouseenter of menu items
   * @param {*} event
   * @param {*} index
   */
  onHeaderMouseenter() {
    this.$refs.body.classList.add('hover-menu');
  }

  /**
   * Mouse leave of menu items
   * @param {*} event
   * @param {*} index
   */
  onHeaderMouseleave() {
    this.$refs.body.classList.remove('hover-menu');
  }

  /**
   * Auto expand textarea
   * @param {Event} e
   */
  onTextareaInput(e) {
    const field = e.target;

    if (!field) return;

    // Reset field height
    field.style.height = 'inherit';

    // Get the computed styles for the element
    const computed = window.getComputedStyle(field);

    // Calculate the height
    const height =
      Number.parseInt(computed.getPropertyValue('border-top-width'), 10) +
      // parseInt(computed.getPropertyValue('padding-top'), 10) +
      field.scrollHeight +
      // parseInt(computed.getPropertyValue('padding-bottom'), 10) +
      Number.parseInt(computed.getPropertyValue('border-bottom-width'), 10);

    field.style.height = `${height}px`;
  }

  /**
   * Footer accordion
   * @param {Event} e
   * @param {number} index
   */
  onFootermenuClick(e, index) {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    // Find the correct element - either by index or from event target
    let footermenu;
    if (typeof index === 'number' && this.$refs.footermenu[index]) {
      footermenu = this.$refs.footermenu[index];
    } else if (e && e.currentTarget) {
      footermenu = e.currentTarget;
    } else if (e && e.target) {
      footermenu = e.target.closest('[data-ref="footermenu[]"]');
    }

    if (!footermenu) return;

    const next = footermenu.nextElementSibling;
    if (next) {
      let height = 0;
      for (let i = 0; i < next.children.length; i += 1) {
        height += Number.parseFloat(next.children[i].offsetHeight);
      }

      gsap.to(next, { duration: 0.3, height: next.offsetHeight === 0 ? `${height}px` : '0px' });
    }
  }

  /**
   *
   */
  onModalClose() {
    Cookies.set('welcome-popin', true);
  }
}

const app = new App(document.documentElement);
app.$mount();
export default app;
